import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import { ASSET_TYPES, calcMortgage, parseInsuranceFile } from '../../../services/investmentPlanningService';
import { computeCollateralCapacity, DEFAULT_COLLATERAL_LTV } from '../../../services/planningTax/insuranceCollateral';

const EMPTY = {
  name: '', type: '', entityId: '', currentBalance: '', monthlyContribution: '',
  avgAnnualReturn: '5', notes: '', linkedPropertyId: '',
  acquisitionDate: '', useFinancing: false, useRevenues: false,
  includePremiumsInCashflow: true,
  premiumDebitAssetId: '',
  adjustedCostBase: '',
  collateralLtvPercent: String(DEFAULT_COLLATERAL_LTV),
  pfiEnabled: false,
  pfiBorrower: 'corporation',
  pfiAdvanceYears: '10',
  pfiInterestRate: '7.5',
  guaranteeFeePercent: '1.5',
  pfiAnnualAdvanceAmount: '',
  pfiStopAdvancesAfterYear: '',
};

/**
 * Calculate remaining mortgage balance after N months of payments.
 * Uses Canadian semi-annual compounding.
 */
function calcRemainingBalance(originalBalance, annualRate, monthlyPayment, monthsElapsed) {
  const r = (parseFloat(annualRate) || 0) / 100;
  const semiRate = r / 2;
  const m = Math.pow(1 + semiRate, 1 / 6) - 1;
  if (m <= 0 || monthlyPayment <= 0) return originalBalance;

  let balance = parseFloat(originalBalance) || 0;
  for (let i = 0; i < monthsElapsed; i++) {
    if (balance <= 0) break;
    const interest = balance * m;
    const principal = Math.max(0, monthlyPayment - interest);
    balance = Math.max(0, balance - principal);
  }
  return balance;
}

const AssetsManager = ({ assets = [], debts = [], entities = [], properties = [], onAdd, onUpdate, onDelete, onAddDebt, onAddRevenue, formatCurrency }) => {
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);

  // Insurance import state
  const [parsedInsurance, setParsedInsurance] = useState(null);
  const [insuranceFileName, setInsuranceFileName] = useState('');
  const [insuranceError, setInsuranceError] = useState('');
  const [currentPolicyYear, setCurrentPolicyYear] = useState(1);
  const insuranceFileRef = useRef(null);

  const entityOptions = entities.map(e => ({ value: e.id, label: e.name }));

  // Properties not yet linked to an asset
  const linkedPropertyIds = assets.filter(a => a.linkedPropertyId).map(a => a.linkedPropertyId);
  const availableProperties = properties.filter(p =>
    !linkedPropertyIds.includes(p.id) || (editingId && assets.find(a => a.id === editingId)?.linkedPropertyId === p.id)
  );

  const propertyOptions = [
    ...availableProperties.map(p => ({
      value: p.id,
      label: `${p.address || 'Sans adresse'} — ${p.property_type || ''} (${p.units || 1} logements)`,
    })),
    { value: '__new__', label: '+ Nouvelle propriété (assistant d\'analyse)' },
  ];

  const selectedProperty = properties.find(p => p.id === form.linkedPropertyId);

  const handleTypeChange = (type) => {
    setForm(prev => ({ ...EMPTY, entityId: prev.entityId, type, includePremiumsInCashflow: true, premiumDebitAssetId: '' }));
    // Reset insurance state on type change
    setParsedInsurance(null);
    setInsuranceFileName('');
    setInsuranceError('');
    setCurrentPolicyYear(1);
    if (insuranceFileRef.current) insuranceFileRef.current.value = '';
  };

  const handleInsuranceFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setInsuranceError('');
    setInsuranceFileName(file.name);
    setForm(prev => ({ ...prev, name: prev.name || file.name.replace(/\.(xlsx|xls|csv)$/i, '') }));

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = new Uint8Array(evt.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const parsed = parseInsuranceFile(workbook);
        if (parsed.length === 0) {
          setInsuranceError("Aucune donnée trouvée. Vérifiez que le fichier contient les colonnes 'Année', 'Valeur de rachat totale'.");
          return;
        }
        setParsedInsurance(parsed);
        setCurrentPolicyYear(parsed[0]?.year || 1);
      } catch (err) {
        setInsuranceError(`Erreur de lecture: ${err.message}`);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handlePropertySelect = (propertyId) => {
    if (propertyId === '__new__') {
      navigate('/property-analysis-wizard?stage=acquired&from=planning');
      return;
    }
    const prop = properties.find(p => p.id === propertyId);
    if (!prop) {
      setForm(prev => ({ ...prev, linkedPropertyId: propertyId }));
      return;
    }
    const value = parseFloat(prop.after_renovation_value) || parseFloat(prop.purchase_price) || 0;
    setForm(prev => ({
      ...prev,
      linkedPropertyId: propertyId,
      name: prop.address || 'Propriété',
      currentBalance: String(value),
      avgAnnualReturn: String(parseFloat(prop.appreciation_rate) || 3),
      monthlyContribution: '0',
      useFinancing: false,
      useRevenues: false,
      acquisitionDate: '',
    }));
  };

  const handleSave = async () => {
    if (!form.type) return;
    if (form.type === 'immobilier' && !form.linkedPropertyId) return;
    if (form.type === 'assurance_participative' && !parsedInsurance && !editingId) return;
    if (form.type !== 'immobilier' && form.type !== 'assurance_participative' && !form.name.trim()) return;
    if (!form.entityId) return;
    if (form.type === 'assurance_participative' && form.includePremiumsInCashflow !== false) {
      if (!form.premiumDebitAssetId) return;
    }

    let data;

    if (form.type === 'assurance_participative' && parsedInsurance) {
      const startIdx = parsedInsurance.findIndex(d => d.year === currentPolicyYear);
      const idx = startIdx >= 0 ? startIdx : 0;
      const currentRow = parsedInsurance[idx] || parsedInsurance[0] || {};

      data = {
        name: form.name || insuranceFileName.replace(/\.(xlsx|xls|csv)$/i, ''),
        type: 'assurance_participative',
        entityId: form.entityId,
        currentBalance: currentRow.surrenderValue || 0,
        monthlyContribution: 0,
        avgAnnualReturn: 0,
        insuranceSchedule: parsedInsurance,
        insuranceCurrentYearIdx: idx,
        totalInsurance: currentRow.totalInsurance || 0,
        annualPremium: currentRow.premiumPaid || 0,
        includePremiumsInCashflow: form.includePremiumsInCashflow !== false,
        premiumDebitAssetId: form.includePremiumsInCashflow !== false ? (form.premiumDebitAssetId || '') : '',
        adjustedCostBase: parseFloat(form.adjustedCostBase) || undefined,
        collateralLtvPercent: parseFloat(form.collateralLtvPercent) || DEFAULT_COLLATERAL_LTV,
        pfiEnabled: !!form.pfiEnabled,
        pfiBorrower: form.pfiBorrower || 'corporation',
        pfiAdvanceYears: parseInt(form.pfiAdvanceYears, 10) || 10,
        pfiInterestRate: parseFloat(form.pfiInterestRate) || 7.5,
        guaranteeFeePercent: parseFloat(form.guaranteeFeePercent) || 0,
        pfiAnnualAdvanceAmount: parseFloat(form.pfiAnnualAdvanceAmount) || 0,
        pfiStopAdvancesAfterYear: form.pfiStopAdvancesAfterYear ? parseInt(form.pfiStopAdvancesAfterYear, 10) : null,
        notes: form.notes || `Importé depuis ${insuranceFileName}. Capital-décès: ${formatCurrency(currentRow.totalInsurance || 0)}`,
      };
    } else {
      data = {
        name: form.name,
        type: form.type,
        entityId: form.entityId,
        linkedPropertyId: form.linkedPropertyId,
        acquisitionDate: form.acquisitionDate,
        currentBalance: parseFloat(form.currentBalance) || 0,
        monthlyContribution: parseFloat(form.monthlyContribution) || 0,
        avgAnnualReturn: parseFloat(form.avgAnnualReturn) || 0,
        notes: form.notes,
      };
    }

    let createdAssetId;
    if (editingId) {
      await onUpdate(editingId, data);
      createdAssetId = editingId;
    } else {
      const created = await onAdd(data);
      createdAssetId = created?.id || '';
    }

    // Auto-create debt from property financing
    if (form.useFinancing && selectedProperty && form.type === 'immobilier' && !editingId) {
      const prop = selectedProperty;
      const originalBalance = parseFloat(prop.mortgage_amount) || 0;
      const rate = parseFloat(prop.interest_rate) || 5.5;
      const amortYears = parseInt(prop.amortization_period) || 25;
      const payment = parseFloat(prop.monthly_payment) || 0;

      let remainingBalance = originalBalance;
      let remainingAmort = amortYears;

      // Adjust for time elapsed since acquisition
      if (form.acquisitionDate) {
        const acqDate = new Date(form.acquisitionDate);
        const now = new Date();
        const monthsElapsed = Math.max(0, (now.getFullYear() - acqDate.getFullYear()) * 12 + (now.getMonth() - acqDate.getMonth()));

        if (monthsElapsed > 0 && payment > 0) {
          remainingBalance = calcRemainingBalance(originalBalance, rate, payment, monthsElapsed);
          remainingAmort = Math.max(1, amortYears - (monthsElapsed / 12));
        }
      }

      // Recalculate payment for remaining balance/amort if balance changed significantly
      let finalPayment = payment;
      if (remainingBalance !== originalBalance) {
        const recalc = calcMortgage(remainingBalance, rate, remainingAmort, 0, 'payment');
        if (recalc > 0) finalPayment = parseFloat(recalc.toFixed(2));
      }

      await onAddDebt({
        name: `Hypothèque - ${prop.address || form.name}`,
        type: 'hypotheque',
        entityId: form.entityId,
        currentBalance: parseFloat(remainingBalance.toFixed(2)),
        interestRate: rate,
        monthlyPayment: finalPayment,
        amortizationRemaining: parseFloat(remainingAmort.toFixed(1)),
        maturityDate: '',
        renewalDate: '',
        linkedAssetId: createdAssetId,
        linkedPropertyId: form.linkedPropertyId,
        notes: form.acquisitionDate ? `Acquis le ${form.acquisitionDate}. Solde ajusté.` : '',
      });
    }

    // Auto-create revenue from property analysis
    if (form.useRevenues && selectedProperty && form.type === 'immobilier' && !editingId) {
      const prop = selectedProperty;
      const monthlyRev = parseFloat(prop.total_monthly_revenue) || 0;
      const monthlyExp = parseFloat(prop.total_monthly_expenses) || 0;
      const netMonthly = monthlyRev - monthlyExp;

      if (netMonthly !== 0) {
        await onAddRevenue({
          name: `Revenus nets - ${prop.address || form.name}`,
          type: 'revenu_immeuble',
          entityId: form.entityId,
          monthlyAmount: parseFloat(netMonthly.toFixed(2)),
          isGross: false,
          linkedAssetId: createdAssetId,
          linkedPropertyId: form.linkedPropertyId,
          reinvest: false,
          reinvestTargetId: '',
          notes: monthlyRev > 0
            ? `Revenus: ${formatCurrency(monthlyRev)} — Dépenses: ${formatCurrency(monthlyExp)}`
            : 'Revenus et dépenses à compléter manuellement',
        });
      }
    }

    setForm({ ...EMPTY, entityId: entities[0]?.id || '' });
    setShowForm(false);
    setEditingId(null);
  };

  const handleEdit = (item) => {
    setForm({
      name: item.name || '', type: item.type || 'non_enregistre', entityId: item.entityId || '',
      currentBalance: String(item.currentBalance || ''), monthlyContribution: String(item.monthlyContribution || ''),
      avgAnnualReturn: String(item.avgAnnualReturn || '5'), notes: item.notes || '',
      linkedPropertyId: item.linkedPropertyId || '', acquisitionDate: item.acquisitionDate || '',
      useFinancing: false, useRevenues: false,
      includePremiumsInCashflow: item.includePremiumsInCashflow !== false,
      premiumDebitAssetId: item.premiumDebitAssetId || '',
      adjustedCostBase: item.adjustedCostBase != null ? String(item.adjustedCostBase) : '',
      collateralLtvPercent: item.collateralLtvPercent != null ? String(item.collateralLtvPercent) : String(DEFAULT_COLLATERAL_LTV),
      pfiEnabled: !!item.pfiEnabled,
      pfiBorrower: item.pfiBorrower || 'corporation',
      pfiAdvanceYears: item.pfiAdvanceYears != null ? String(item.pfiAdvanceYears) : '10',
      pfiInterestRate: item.pfiInterestRate != null ? String(item.pfiInterestRate) : '7.5',
      guaranteeFeePercent: item.guaranteeFeePercent != null ? String(item.guaranteeFeePercent) : '1.5',
      pfiAnnualAdvanceAmount: item.pfiAnnualAdvanceAmount ? String(item.pfiAnnualAdvanceAmount) : '',
      pfiStopAdvancesAfterYear: item.pfiStopAdvancesAfterYear != null ? String(item.pfiStopAdvancesAfterYear) : '',
    });
    if (item.type === 'assurance_participative' && item.insuranceSchedule?.length) {
      setParsedInsurance(item.insuranceSchedule);
      const y = item.insuranceSchedule[item.insuranceCurrentYearIdx || 0]?.year ?? item.insuranceSchedule[0]?.year;
      if (y != null) setCurrentPolicyYear(y);
    } else {
      setParsedInsurance(null);
      setCurrentPolicyYear(1);
    }
    setEditingId(item.id);
    setShowForm(true);
  };

  const handleCancel = () => {
    setForm({ ...EMPTY, entityId: entities[0]?.id || '' });
    setShowForm(false);
    setEditingId(null);
    setParsedInsurance(null);
    setInsuranceFileName('');
    setInsuranceError('');
    setCurrentPolicyYear(1);
    if (insuranceFileRef.current) insuranceFileRef.current.value = '';
  };

  const openForm = () => {
    setForm({ ...EMPTY, entityId: entities[0]?.id || '' });
    setEditingId(null);
    setShowForm(true);
    setParsedInsurance(null);
    setInsuranceFileName('');
    setInsuranceError('');
    setCurrentPolicyYear(1);
  };

  const getEntityName = (id) => entities.find(e => e.id === id)?.name || '—';
  const getTypeName = (type) => ASSET_TYPES.find(t => t.value === type)?.label || type;
  const getPropertyLabel = (id) => {
    const p = properties.find(pr => pr.id === id);
    return p ? (p.address || 'Propriété') : '—';
  };

  const totalBalance = assets.reduce((s, a) => s + (parseFloat(a.currentBalance) || 0), 0);

  const premiumDebitAssetOptions = assets
    .filter(a => a.type !== 'immobilier' && a.type !== 'assurance_participative' && a.id !== editingId)
    .map(a => ({
      value: a.id,
      label: `${a.name} — ${getEntityName(a.entityId)}`,
    }));

  // Check if selected property has financing/revenue data
  const hasFinancing = selectedProperty && (parseFloat(selectedProperty.mortgage_amount) || 0) > 0;
  const hasRevenues = selectedProperty && ((parseFloat(selectedProperty.total_monthly_revenue) || 0) > 0 || (parseFloat(selectedProperty.total_monthly_expenses) || 0) > 0);

  // Preview calculations
  let financingPreview = null;
  if (form.useFinancing && selectedProperty && form.acquisitionDate) {
    const originalBal = parseFloat(selectedProperty.mortgage_amount) || 0;
    const rate = parseFloat(selectedProperty.interest_rate) || 5.5;
    const payment = parseFloat(selectedProperty.monthly_payment) || 0;
    const amort = parseInt(selectedProperty.amortization_period) || 25;
    const acqDate = new Date(form.acquisitionDate);
    const now = new Date();
    const months = Math.max(0, (now.getFullYear() - acqDate.getFullYear()) * 12 + (now.getMonth() - acqDate.getMonth()));
    const remaining = calcRemainingBalance(originalBal, rate, payment, months);
    financingPreview = {
      originalBalance: originalBal,
      remainingBalance: remaining,
      monthsElapsed: months,
      remainingAmort: Math.max(1, amort - months / 12),
      payment,
      rate,
    };
  }

  const renderTypeFields = () => {
    if (!form.type) return null;

    if (form.type === 'immobilier') {
      return (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Select
              label="Propriété" value={form.linkedPropertyId} onChange={handlePropertySelect}
              options={propertyOptions} placeholder="Sélectionner une propriété..." required
              data-testid="asset-property-select"
            />
            <Select label="Détenteur" value={form.entityId} onChange={v => setForm({ ...form, entityId: v })}
              options={entityOptions} placeholder="Sélectionner..." required />
          </div>

          {form.linkedPropertyId && form.linkedPropertyId !== '__new__' && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Input label="Nom / Adresse" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="asset-name-input" />
                <Input label="Valeur actuelle ($)" type="number" value={form.currentBalance} onChange={e => setForm({ ...form, currentBalance: e.target.value })} data-testid="asset-value-input" />
                <Input label="Appréciation annuelle (%)" type="number" value={form.avgAnnualReturn} onChange={e => setForm({ ...form, avgAnnualReturn: e.target.value })} placeholder="3" />
              </div>

              {/* Acquisition date */}
              <Input
                label="Date d'acquisition"
                type="date"
                value={form.acquisitionDate}
                onChange={e => setForm({ ...form, acquisitionDate: e.target.value })}
                data-testid="asset-acquisition-date"
              />

              {/* Use financing checkbox */}
              {hasFinancing && !editingId && (
                <div className="border border-border rounded-lg p-3 space-y-2" data-testid="use-financing-section">
                  <label className="flex items-center gap-2.5 cursor-pointer" data-testid="use-financing-toggle">
                    <input
                      type="checkbox" checked={form.useFinancing}
                      onChange={e => setForm({ ...form, useFinancing: e.target.checked })}
                      className="h-4 w-4 rounded border-input accent-primary"
                    />
                    <Icon name="Landmark" size={16} className="text-primary" />
                    <div>
                      <span className="text-sm font-medium text-foreground">Utiliser le financement de l'analyse</span>
                      <p className="text-xs text-muted-foreground">
                        Hypothèque de {formatCurrency(selectedProperty.mortgage_amount)} à {selectedProperty.interest_rate}% sur {selectedProperty.amortization_period} ans — Paiement {formatCurrency(selectedProperty.monthly_payment)}/mois
                      </p>
                    </div>
                  </label>

                  {/* Preview adjusted values */}
                  {financingPreview && (
                    <div className="ml-7 p-2.5 rounded-md bg-primary/5 border border-primary/10 space-y-1" data-testid="financing-preview">
                      <p className="text-xs font-medium text-primary flex items-center gap-1.5">
                        <Icon name="Calculator" size={12} />
                        Ajustement selon {financingPreview.monthsElapsed} mois écoulés
                      </p>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                        <div>
                          <span className="text-muted-foreground">Solde original</span>
                          <p className="font-medium" style={{ fontFamily: 'var(--font-data)' }}>{formatCurrency(financingPreview.originalBalance)}</p>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Solde restant</span>
                          <p className="font-semibold text-primary" style={{ fontFamily: 'var(--font-data)' }}>{formatCurrency(financingPreview.remainingBalance)}</p>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Capital remboursé</span>
                          <p className="font-medium" style={{ fontFamily: 'var(--font-data)', color: 'var(--color-success)' }}>
                            {formatCurrency(financingPreview.originalBalance - financingPreview.remainingBalance)}
                          </p>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Amort. restant</span>
                          <p className="font-medium" style={{ fontFamily: 'var(--font-data)' }}>{financingPreview.remainingAmort.toFixed(1)} ans</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {form.useFinancing && !form.acquisitionDate && (
                    <p className="ml-7 text-xs text-warning flex items-center gap-1">
                      <Icon name="AlertTriangle" size={12} />
                      Entrez la date d'acquisition pour ajuster le solde hypothécaire
                    </p>
                  )}
                </div>
              )}

              {!hasFinancing && !editingId && (
                <div className="flex items-center gap-2 p-2.5 rounded-lg bg-muted/50 text-xs text-muted-foreground">
                  <Icon name="Info" size={14} />
                  <span>Aucun financement dans l'analyse. Ajoutez une dette manuellement si besoin.</span>
                </div>
              )}

              {/* Use revenues checkbox */}
              {!editingId && (
                <div className="border border-border rounded-lg p-3" data-testid="use-revenues-section">
                  <label className={`flex items-center gap-2.5 cursor-pointer ${!hasRevenues ? 'opacity-60' : ''}`} data-testid="use-revenues-toggle">
                    <input
                      type="checkbox" checked={form.useRevenues}
                      onChange={e => setForm({ ...form, useRevenues: e.target.checked })}
                      className="h-4 w-4 rounded border-input accent-primary"
                      disabled={!hasRevenues}
                    />
                    <Icon name="Banknote" size={16} style={{ color: 'var(--color-success)' }} />
                    <div>
                      <span className="text-sm font-medium text-foreground">Utiliser les revenus et dépenses de l'analyse</span>
                      {hasRevenues ? (
                        <p className="text-xs text-muted-foreground">
                          Revenus: {formatCurrency(selectedProperty.total_monthly_revenue)}/mois — Dépenses: {formatCurrency(selectedProperty.total_monthly_expenses)}/mois
                          — <span className="font-semibold" style={{ color: 'var(--color-success)' }}>
                            Net: {formatCurrency((parseFloat(selectedProperty.total_monthly_revenue) || 0) - (parseFloat(selectedProperty.total_monthly_expenses) || 0))}/mois
                          </span>
                        </p>
                      ) : (
                        <p className="text-xs text-muted-foreground">Aucun revenu/dépense dans l'analyse. Ajoutez un revenu manuellement.</p>
                      )}
                    </div>
                  </label>
                </div>
              )}
            </>
          )}
        </>
      );
    }

    // Assurance participative: CSV/XLSX import form
    if (form.type === 'assurance_participative') {
      const lastRow = parsedInsurance?.[parsedInsurance.length - 1];
      const currentIdx = parsedInsurance?.findIndex(d => d.year === currentPolicyYear);
      const currentRow = currentIdx >= 0 ? parsedInsurance[currentIdx] : null;

      return (
        <>
          <div className="border border-border rounded-lg p-4 space-y-3" data-testid="insurance-import-section">
            <div className="flex items-center gap-2 mb-1">
              <Icon name="Shield" size={16} className="text-primary" />
              <span className="text-sm font-semibold text-foreground">Import de la police d'assurance participative</span>
            </div>
            <p className="text-xs text-muted-foreground">
              Importez un fichier XLSX ou CSV. Colonnes recherchées : Année, Valeur de rachat totale, Montant d'assurance vie total.
            </p>

            <input
              ref={insuranceFileRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={handleInsuranceFile}
              className="text-sm text-foreground file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border file:border-border file:text-sm file:font-medium file:bg-card file:text-foreground file:cursor-pointer hover:file:bg-muted"
              data-testid="insurance-file-input"
            />

            {insuranceError && (
              <div className="flex items-center gap-2 p-2.5 rounded-lg bg-destructive/10 border border-destructive/20 text-sm text-destructive">
                <Icon name="AlertCircle" size={16} />
                {insuranceError}
              </div>
            )}

            {parsedInsurance && (
              <>
                <div className="border border-border rounded-lg p-3 space-y-3" data-testid="insurance-preview">
                  <div className="flex items-center gap-2">
                    <Icon name="CheckCircle" size={16} className="text-success" />
                    <span className="text-sm font-medium text-foreground">
                      {parsedInsurance.length} années de données trouvées
                    </span>
                  </div>

                  {/* Summary cards */}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    <div className="bg-card rounded-md p-2.5 border border-border">
                      <p className="text-xs text-muted-foreground" style={{ fontFamily: 'var(--font-caption)' }}>Valeur de rachat (dernière année)</p>
                      <p className="text-sm font-semibold text-primary" style={{ fontFamily: 'var(--font-data)' }}>
                        {formatCurrency(lastRow?.surrenderValue || 0)}
                      </p>
                    </div>
                    <div className="bg-card rounded-md p-2.5 border border-border">
                      <p className="text-xs text-muted-foreground" style={{ fontFamily: 'var(--font-caption)' }}>Assurance vie totale</p>
                      <p className="text-sm font-semibold" style={{ fontFamily: 'var(--font-data)', color: 'var(--color-success)' }}>
                        {formatCurrency(lastRow?.totalInsurance || 0)}
                      </p>
                    </div>
                    <div className="bg-card rounded-md p-2.5 border border-border">
                      <p className="text-xs text-muted-foreground" style={{ fontFamily: 'var(--font-caption)' }}>Primes cumulatives</p>
                      <p className="text-sm font-semibold text-foreground" style={{ fontFamily: 'var(--font-data)' }}>
                        {formatCurrency(parsedInsurance.reduce((s, d) => s + (d.premiumPaid || 0), 0))}
                      </p>
                    </div>
                  </div>

                  {/* Data preview table */}
                  <div className="max-h-[160px] overflow-y-auto">
                    <table className="w-full text-xs" data-testid="insurance-preview-table">
                      <thead className="sticky top-0 bg-muted">
                        <tr className="text-left text-muted-foreground border-b border-border">
                          <th className="pb-1.5 pr-2">Année</th>
                          <th className="pb-1.5 pr-2">Âge</th>
                          <th className="pb-1.5 pr-2 text-right">Valeur de rachat</th>
                          <th className="pb-1.5 pr-2 text-right">Assurance vie</th>
                          <th className="pb-1.5 text-right">Prime annuelle</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {parsedInsurance.slice(0, 8).map((row, i) => (
                          <tr key={i} className={row.year === currentPolicyYear ? 'bg-primary/10' : 'text-foreground'}>
                            <td className="py-1 pr-2">{row.year}{row.year === currentPolicyYear ? ' *' : ''}</td>
                            <td className="py-1 pr-2 text-muted-foreground">{row.age || '—'}</td>
                            <td className="py-1 pr-2 text-right" style={{ fontFamily: 'var(--font-data)' }}>{formatCurrency(row.surrenderValue)}</td>
                            <td className="py-1 pr-2 text-right" style={{ fontFamily: 'var(--font-data)' }}>{formatCurrency(row.totalInsurance)}</td>
                            <td className="py-1 text-right" style={{ fontFamily: 'var(--font-data)' }}>{formatCurrency(row.premiumPaid)}</td>
                          </tr>
                        ))}
                        {parsedInsurance.length > 8 && (
                          <tr><td colSpan={5} className="py-1 text-center text-muted-foreground">... {parsedInsurance.length - 8} lignes supplémentaires</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Current year info */}
                {currentRow && (
                  <div className="flex items-center gap-2 p-2.5 rounded-md bg-primary/5 border border-primary/10 text-xs">
                    <Icon name="Info" size={14} className="text-primary shrink-0" />
                    <span className="text-foreground">
                      Année courante ({currentPolicyYear}): Valeur de rachat = <strong>{formatCurrency(currentRow.surrenderValue)}</strong>, Capital-décès = <strong>{formatCurrency(currentRow.totalInsurance)}</strong>
                    </span>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="border border-primary/20 rounded-lg p-4 space-y-3 bg-primary/5" data-testid="insurance-collateral-section">
            <p className="text-sm font-semibold text-foreground flex items-center gap-2">
              <Icon name="Landmark" size={16} className="text-primary" />
              Collatéral et PFI
            </p>
            {(() => {
              const previewPolicy = {
                id: editingId || 'preview',
                type: 'assurance_participative',
                currentBalance: currentRow?.surrenderValue || 0,
                collateralLtvPercent: parseFloat(form.collateralLtvPercent) || DEFAULT_COLLATERAL_LTV,
              };
              const cap = computeCollateralCapacity(previewPolicy, debts);
              return (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                  <div className="rounded-md border border-border bg-card p-2"><p className="text-muted-foreground">VR</p><p className="font-semibold">{formatCurrency(cap.surrenderValue)}</p></div>
                  <div className="rounded-md border border-border bg-card p-2"><p className="text-muted-foreground">Encours</p><p className="font-semibold text-destructive">{formatCurrency(cap.linkedDebt)}</p></div>
                  <div className="rounded-md border border-border bg-card p-2"><p className="text-muted-foreground">Capacité</p><p className="font-semibold text-primary">{formatCurrency(cap.availableCapacity)}</p></div>
                  <div className="rounded-md border border-border bg-card p-2"><p className="text-muted-foreground">Capital décès</p><p className="font-semibold">{formatCurrency(currentRow?.totalInsurance || 0)}</p></div>
                </div>
              );
            })()}
            <div className="grid grid-cols-2 gap-3">
              <Input label="CBR $" type="number" value={form.adjustedCostBase} onChange={(e) => setForm({ ...form, adjustedCostBase: e.target.value })} />
              <Input label="LTV %" type="number" value={form.collateralLtvPercent} onChange={(e) => setForm({ ...form, collateralLtvPercent: e.target.value })} />
            </div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!form.pfiEnabled} onChange={(e) => setForm({ ...form, pfiEnabled: e.target.checked })} />PFI en projection</label>
            {form.pfiEnabled && (
              <div className="grid grid-cols-2 gap-3">
                <Input label="Années avances" type="number" value={form.pfiAdvanceYears} onChange={(e) => setForm({ ...form, pfiAdvanceYears: e.target.value })} />
                <Input label="Taux %" type="number" value={form.pfiInterestRate} onChange={(e) => setForm({ ...form, pfiInterestRate: e.target.value })} />
                <Select label="Emprunteur" value={form.pfiBorrower} onChange={(v) => setForm({ ...form, pfiBorrower: v })} options={[{ value: 'corporation', label: 'Société' }, { value: 'shareholder', label: 'Actionnaire' }]} />
                <Input label="Frais garantie %" type="number" value={form.guaranteeFeePercent} onChange={(e) => setForm({ ...form, guaranteeFeePercent: e.target.value })} />
              </div>
            )}
          </div>

          <label className="flex items-start gap-2.5 cursor-pointer p-3 rounded-lg border border-border bg-muted/30" data-testid="asset-insurance-premiums-cashflow-toggle">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-input accent-primary mt-0.5"
              checked={form.includePremiumsInCashflow !== false}
              onChange={e => setForm({ ...form, includePremiumsInCashflow: e.target.checked })}
            />
            <span>
              <span className="text-sm font-medium text-foreground">Inclure les primes dans le cashflow</span>
              <span className="block text-xs text-muted-foreground mt-0.5">
                Si coché, la prime annuelle réduit le cashflow disponible par détenteur (comme une dépense). Sinon, seule la valeur de rachat est projetée.
              </span>
            </span>
          </label>

          {form.includePremiumsInCashflow !== false && parsedInsurance?.length > 0 && (
            <Select
              label="Compte de prélèvement des primes"
              value={form.premiumDebitAssetId || ''}
              onChange={v => setForm({ ...form, premiumDebitAssetId: v })}
              options={premiumDebitAssetOptions}
              placeholder={premiumDebitAssetOptions.length === 0 ? 'Ajoutez d&apos;abord un compte liquide au bilan' : 'Sélectionner le compte débité...'}
              required
              data-testid="asset-insurance-premium-debit"
            />
          )}

          {/* Name, Entity, Current year */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Input label="Nom de la police" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
              placeholder="Ex: Assurance participative Sun Life" required data-testid="asset-name-input" />
            <Select label="Détenteur" value={form.entityId} onChange={v => setForm({ ...form, entityId: v })}
              options={entityOptions} placeholder="Sélectionner..." required />
            {parsedInsurance && parsedInsurance.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Année courante de la police</label>
                <select
                  value={currentPolicyYear}
                  onChange={e => setCurrentPolicyYear(parseInt(e.target.value))}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground"
                  data-testid="insurance-current-year-select"
                >
                  {parsedInsurance.map(row => (
                    <option key={row.year} value={row.year}>
                      Année {row.year}{row.age ? ` (âge ${row.age})` : ''} — Rachat: {formatCurrency(row.surrenderValue)}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-muted-foreground mt-1">Où en êtes-vous dans votre police ?</p>
              </div>
            )}
          </div>
        </>
      );
    }

    // All other types: standard financial instrument fields
    return (
      <>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Input label="Nom du compte" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
            placeholder={getPlaceholder(form.type)} required data-testid="asset-name-input" />
          <Select label="Détenteur" value={form.entityId} onChange={v => setForm({ ...form, entityId: v })}
            options={entityOptions} placeholder="Sélectionner..." required />
          <Input label="Solde actuel ($)" type="number" value={form.currentBalance}
            onChange={e => setForm({ ...form, currentBalance: e.target.value })} placeholder="50 000" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Input label="Cotisation mensuelle ($)" type="number" value={form.monthlyContribution}
            onChange={e => setForm({ ...form, monthlyContribution: e.target.value })} placeholder="500" />
          <Input label="Rendement moyen annuel (%)" type="number" value={form.avgAnnualReturn}
            onChange={e => setForm({ ...form, avgAnnualReturn: e.target.value })} placeholder="5" />
        </div>
      </>
    );
  };

  return (
    <div className="bg-card border border-border rounded-xl p-5 md:p-6" data-testid="assets-manager">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-foreground flex items-center gap-2" style={{ fontFamily: 'var(--font-heading)' }}>
            <Icon name="PiggyBank" size={20} className="text-success" />
            Actifs & Épargne
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">Total: <span className="font-semibold" style={{ fontFamily: 'var(--font-data)' }}>{formatCurrency(totalBalance)}</span></p>
        </div>
        <Button size="sm" iconName="Plus" iconPosition="left" onClick={openForm} data-testid="add-asset-btn">Ajouter</Button>
      </div>

      {showForm && (
        <div className="bg-muted rounded-lg p-4 mb-4 space-y-3" data-testid="asset-form">
          <Select label="Type d'actif" value={form.type} onChange={handleTypeChange} options={ASSET_TYPES}
            placeholder="Choisir le type d'actif..." required data-testid="asset-type-select" />
          {renderTypeFields()}
          {form.type && (
            <>
              <Input label="Notes" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Optionnel" />
              <div className="flex gap-2">
                <Button size="sm" onClick={handleSave} data-testid="asset-save-btn">{editingId ? 'Mettre à jour' : 'Ajouter'}</Button>
                <Button size="sm" variant="outline" onClick={handleCancel} data-testid="asset-cancel-btn">Annuler</Button>
              </div>
            </>
          )}
        </div>
      )}

      {assets.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-6">Aucun actif enregistré.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="assets-table">
            <thead>
              <tr className="text-left text-muted-foreground border-b border-border">
                <th className="pb-2 pr-3" style={{ fontFamily: 'var(--font-caption)' }}>Nom</th>
                <th className="pb-2 pr-3" style={{ fontFamily: 'var(--font-caption)' }}>Type</th>
                <th className="pb-2 pr-3" style={{ fontFamily: 'var(--font-caption)' }}>Détenteur</th>
                <th className="pb-2 pr-3 text-right" style={{ fontFamily: 'var(--font-caption)' }}>Valeur</th>
                <th className="pb-2 pr-3 text-right" style={{ fontFamily: 'var(--font-caption)' }}>Cotisation/mois</th>
                <th className="pb-2 pr-3 text-right" style={{ fontFamily: 'var(--font-caption)' }}>Rendement</th>
                <th className="pb-2 w-20"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {assets.map(item => (
                <tr key={item.id} className="hover:bg-muted/30 transition-colors" data-testid={`asset-row-${item.id}`}>
                  <td className="py-2.5 pr-3">
                    <div className="flex items-center gap-2">
                      {item.type === 'immobilier' && <Icon name="Home" size={14} className="text-primary shrink-0" />}
                      {item.type === 'assurance_participative' && <Icon name="Shield" size={14} className="text-primary shrink-0" />}
                      <span className="font-medium text-foreground">{item.name}</span>
                    </div>
                    {item.acquisitionDate && (
                      <p className="text-xs text-muted-foreground mt-0.5">Acquis: {item.acquisitionDate}</p>
                    )}
                    {item.type === 'assurance_participative' && item.totalInsurance > 0 && (
                      <p className="text-xs text-muted-foreground mt-0.5">Capital-décès: {formatCurrency(item.totalInsurance)}</p>
                    )}
                    {item.type === 'assurance_participative' && item.pfiEnabled && (
                      <span className="inline-block mt-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-800">PFI actif</span>
                    )}
                  </td>
                  <td className="py-2.5 pr-3 text-muted-foreground">{getTypeName(item.type)}</td>
                  <td className="py-2.5 pr-3 text-muted-foreground">{getEntityName(item.entityId)}</td>
                  <td className="py-2.5 pr-3 text-right font-medium" style={{ fontFamily: 'var(--font-data)' }}>{formatCurrency(item.currentBalance)}</td>
                  <td className="py-2.5 pr-3 text-right text-muted-foreground" style={{ fontFamily: 'var(--font-data)' }}>
                    {item.type === 'immobilier' || item.type === 'assurance_participative' ? '—' : formatCurrency(item.monthlyContribution)}
                  </td>
                  <td className="py-2.5 pr-3 text-right text-muted-foreground" style={{ fontFamily: 'var(--font-data)' }}>
                    {item.type === 'assurance_participative' ? '—' : `${item.avgAnnualReturn}%`}
                  </td>
                  <td className="py-2.5">
                    <div className="flex gap-1 justify-end">
                      <Button size="xs" variant="ghost" iconName="Pencil" onClick={() => handleEdit(item)} />
                      <Button size="xs" variant="ghost" iconName="Trash2" onClick={() => onDelete(item.id)} style={{ color: 'var(--color-destructive)' }} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

function getPlaceholder(type) {
  const placeholders = {
    reer: 'Ex: REER Desjardins', celi: 'Ex: CELI Questrade',
    non_enregistre: 'Ex: Compte placement Wealthsimple', corporatif: 'Ex: Compte corporatif Gestion ABC',
    autre: 'Ex: Cryptomonnaie, Or, etc.',
  };
  return placeholders[type] || 'Nom du compte';
}

export default AssetsManager;
